import {
    runSavePrep,
    runCompare,
    createDraftSavePayloadCache,
    getMainThreadAuthoringNormalizers,
} from "./authoring-schedule-compute-core"
import { isScheduleSavePerfEnabled, logScheduleSavePerf } from "./schedule-save-perf"

const clients = new Map()

/**
 * @param {{ draftId: string }} params
 * @returns {AuthoringScheduleWorkerClient}
 */
export function getAuthoringScheduleWorkerClient({ draftId }) {
    if (!draftId) {
        throw new Error("getAuthoringScheduleWorkerClient requires draftId")
    }

    if (!clients.has(draftId)) {
        clients.set(draftId, new AuthoringScheduleWorkerClient({ draftId }))
    }

    return clients.get(draftId)
}

export function releaseAuthoringScheduleWorkerClient(draftId) {
    const client = clients.get(draftId)
    if (client) {
        client.terminate()
    }
}

export function __resetAuthoringScheduleWorkerClientsForTests() {
    for (const client of clients.values()) {
        client.terminate()
    }
    clients.clear()
}

class AuthoringScheduleWorkerClient {
    constructor({ draftId }) {
        this.draftId = draftId
        this.generation = 0
        this.worker = null
        this.useFallback = !canUseWorker()
        this.fallbackCache = createDraftSavePayloadCache()

        this.savePrepJobId = 0
        this.savePrepDirty = true
        this.savePrepLocked = false
        this.savePrepPayload = null
        this.pendingSavePrep = null

        this.compareJobId = 0
        this.pendingCompare = null
        this.latestCompareResult = null
        this.latestCompareJobId = 0

        this.warmedGeneration = -1
        this.pendingWarm = null

        if (!this.useFallback) {
            try {
                this.worker = new Worker(new URL("./authoring-schedule-compute.worker.js", import.meta.url), {
                    type: "module",
                })
                this.worker.onmessage = (event) => this.handleWorkerMessage(event.data)
                this.worker.onerror = (error) => {
                    console.error("[authoring-schedule-worker]", error)
                    this.switchToFallback("worker-error")
                }
            } catch (error) {
                console.warn("[authoring-schedule-worker] falling back to main thread", error)
                this.switchToFallback("worker-init-failed")
            }
        }
    }

    reset() {
        this.generation += 1
        this.savePrepDirty = true
        this.savePrepPayload = null
        this.pendingSavePrep = null
        this.cancelPendingCompare()
        this.latestCompareResult = null
        this.latestCompareJobId = 0
        this.warmedGeneration = -1
        if (this.pendingWarm) {
            this.pendingWarm.resolve?.()
            this.pendingWarm = null
        }

        this.fallbackCache.reset()

        if (this.worker) {
            this.worker.postMessage({ type: "resetCache" })
        }
    }

    terminate() {
        if (this.worker) {
            this.worker.terminate()
            this.worker = null
        }
        clients.delete(this.draftId)
    }

    markSavePrepDirty() {
        this.savePrepDirty = true
        this.savePrepPayload = null
        this.pendingSavePrep = null
    }

    scheduleSavePrep(params) {
        if (this.savePrepLocked) return this.savePrepJobId

        this.savePrepJobId += 1
        const jobId = this.savePrepJobId
        const { generation } = this
        const perfStart = isScheduleSavePerfEnabled() ? performance.now() : 0

        if (this.useFallback) {
            this.savePrepPayload = runSavePrep({ ...params, cache: this.fallbackCache })
            this.savePrepDirty = false
            logWorkerPerf("savePrep.fallback", params, perfStart)
            return jobId
        }

        this.pendingSavePrep = { jobId, generation, params }
        this.worker.postMessage({
            type: "savePrep",
            jobId,
            generation,
            draft: params.draft,
            macroMap: params.macroMap,
            macroMapKey: params.macroMapKey ?? "",
            scopes: params.scopes ?? [],
        })
        logWorkerPerf("savePrep.post", params, perfStart)
        return jobId
    }

    async consumeSavePrep() {
        this.savePrepLocked = true

        if (this.useFallback) {
            return this.savePrepDirty ? null : this.savePrepPayload
        }

        if (this.pendingSavePrep) {
            await waitForSavePrep(this, this.pendingSavePrep.jobId, this.pendingSavePrep.generation)
        }

        if (this.savePrepDirty) {
            return null
        }

        return this.savePrepPayload
    }

    releaseSavePrepLock() {
        this.savePrepLocked = false
    }

    /**
     * @param {object} params
     * @param {{ priority?: boolean }} [options]
     * @returns {number}
     */
    scheduleCompare(params, { priority = false } = {}) {
        this.cancelPendingCompare()

        this.compareJobId += 1
        const jobId = this.compareJobId
        const { generation } = this
        const perfStart = isScheduleSavePerfEnabled() ? performance.now() : 0

        if (this.useFallback) {
            const result = runCompare({
                ...params,
                compareLoading: false,
                dirty: false,
                normalizers: getMainThreadAuthoringNormalizers(),
            })
            this.latestCompareResult = result
            this.latestCompareJobId = jobId
            logWorkerPerf("compare.fallback", params, perfStart)
            return jobId
        }

        const pending = { jobId, generation, priority, cancelled: false, resolve: null, promise: null }
        pending.promise = new Promise((resolve) => {
            pending.resolve = resolve
        })
        this.pendingCompare = pending

        this.worker.postMessage({
            type: "compare",
            jobId,
            generation,
            draft: params.draft,
            compareSource: params.compareSource,
            macroMap: params.macroMap,
        })
        logWorkerPerf("compare.post", params, perfStart)
        return jobId
    }

    async waitForCompare(jobId, { priority = false } = {}) {
        if (this.useFallback) {
            return jobId === this.latestCompareJobId ? this.latestCompareResult : null
        }

        if (jobId === this.latestCompareJobId && this.latestCompareResult !== null && !this.pendingCompare) {
            return this.latestCompareResult
        }

        if (!this.pendingCompare || this.pendingCompare.jobId !== jobId) {
            if (jobId === this.latestCompareJobId) {
                return this.latestCompareResult
            }
            return null
        }

        const result = await this.pendingCompare.promise
        if (this.pendingCompare?.cancelled) {
            if (jobId === this.latestCompareJobId) {
                return this.latestCompareResult
            }
            return null
        }
        if (priority || jobId === this.latestCompareJobId) {
            return result
        }
        return null
    }

    cancelPendingCompare() {
        if (!this.pendingCompare) return
        const pending = this.pendingCompare
        this.pendingCompare = null
        pending.cancelled = true
        pending.resolve?.(null)
    }

    async getCompareResult({ jobId, priority = false } = {}) {
        return this.waitForCompare(jobId, { priority })
    }

    warm() {
        if (this.useFallback) {
            return Promise.resolve()
        }

        if (this.warmedGeneration === this.generation) {
            return Promise.resolve()
        }

        if (this.pendingWarm) {
            return this.pendingWarm.promise
        }

        const { generation } = this
        const perfStart = isScheduleSavePerfEnabled() ? performance.now() : 0
        let resolveWarm

        const promise = new Promise((resolve) => {
            resolveWarm = resolve
        })

        this.pendingWarm = { generation, resolve: resolveWarm, promise }

        this.worker.postMessage({ type: "warm", generation })
        logWorkerPerf("warm.post", {}, perfStart)

        return promise
    }

    runSavePrepFallback(params) {
        return runSavePrep({ ...params, cache: this.fallbackCache })
    }

    handleWorkerMessage(message) {
        if (!message || message.generation !== this.generation) {
            return
        }

        if (message.type === "savePrepResult") {
            if (this.pendingSavePrep?.jobId !== message.jobId) {
                return
            }

            this.savePrepPayload = message.payload
            this.savePrepDirty = false
            this.pendingSavePrep = null

            if (isScheduleSavePerfEnabled()) {
                logScheduleSavePerf("savePrep.workerResult", {
                    jobId: message.jobId,
                    stats: message.stats,
                })
            }
            return
        }

        if (message.type === "compareResult") {
            if (this.pendingCompare?.cancelled || this.pendingCompare?.jobId !== message.jobId) {
                return
            }

            this.latestCompareResult = message.result
            this.latestCompareJobId = message.jobId
            const pending = this.pendingCompare
            this.pendingCompare = null
            pending.resolve?.(message.result)

            if (isScheduleSavePerfEnabled()) {
                logScheduleSavePerf("compare.workerResult", {
                    jobId: message.jobId,
                    result: message.result,
                })
            }
            return
        }

        if (message.type === "warmComplete") {
            if (this.pendingWarm?.generation !== message.generation) {
                return
            }

            this.warmedGeneration = message.generation
            const pending = this.pendingWarm
            this.pendingWarm = null
            pending.resolve?.()

            if (isScheduleSavePerfEnabled()) {
                logScheduleSavePerf("warm.complete", { generation: message.generation })
            }
            return
        }

        if (message.type === "error") {
            console.error("[authoring-schedule-worker]", message.message)
        }
    }

    switchToFallback(reason) {
        this.useFallback = true
        if (this.worker) {
            this.worker.terminate()
            this.worker = null
        }
        if (isScheduleSavePerfEnabled()) {
            logScheduleSavePerf("worker.fallback", { reason, draftId: this.draftId })
        }
    }
}

function canUseWorker() {
    return typeof Worker !== "undefined" && typeof window !== "undefined"
}

function waitForSavePrep(client, jobId, generation) {
    return new Promise((resolve) => {
        const started = performance.now()

        function poll() {
            if (generation !== client.generation) {
                resolve(null)
                return
            }

            if (client.savePrepJobId !== jobId) {
                resolve(client.savePrepPayload)
                return
            }

            if (!client.pendingSavePrep && !client.savePrepDirty) {
                resolve(client.savePrepPayload)
                return
            }

            if (performance.now() - started > 5000) {
                resolve(null)
                return
            }

            setTimeout(poll, 16)
        }

        poll()
    })
}

function logWorkerPerf(label, params, perfStart) {
    if (!isScheduleSavePerfEnabled()) return

    logScheduleSavePerf(label, {
        draftId: params?.draft?.id,
        scopeCount: params?.scopes?.length ?? 0,
        taskCount: params?.draft?.tasks?.length ?? 0,
        durationMs: Math.round(performance.now() - perfStart),
    })
}
